import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import Filters from "../components/LogsByDay/Filters";
import LogsTable from "../components/LogsByDay/LogsTable";
import Pagination from "../components/LogsByDay/Pagination";
import ErrorBox from "../components/LogsByDay/ErrorBox";

export default function LogsByDay() {
    const [health, setHealth] = useState("Đang kiểm tra...");

    const [filters, setFilters] = useState({
        day: "",
        startTime: "",
        endTime: "",
        clientMsgId: "",
    });

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const formatDayForAPI = (d) => d.replaceAll("-", "");

    // Default init
    useEffect(() => {
        const now = new Date();
        const plus1min = new Date(now.getTime() + 1 * 60 * 1000);

        const toDateInput = (d) => d.toISOString().slice(0, 10);
        const toTimeInput = (d) => d.toTimeString().slice(0, 5);

        setFilters({
            day: toDateInput(now),
            startTime: toTimeInput(now),
            endTime: toTimeInput(plus1min),
            clientMsgId: "",
        });
    }, []);

    // Health check
    useEffect(() => {
        axios
            .get("/logs/health")
            .then(() => setHealth("✅ Cassandra"))
            .catch(() => setHealth("❌ Cassandra"));
    }, []);

    // Validate + Build payload
    const buildPayload = () => {
        setErrorMsg("");

        const { day, startTime, endTime, clientMsgId } = filters;

        if (!day || !startTime || !endTime) {
            setErrorMsg("Vui lòng chọn ngày và khoảng thời gian.");
            return null;
        }

        const start = new Date(`${day}T${startTime}:00`);
        const end = new Date(`${day}T${endTime}:00`);

        if (end <= start) {
            setErrorMsg("Thời gian kết thúc phải lớn hơn thời gian bắt đầu.");
            return null;
        }

        const diff = end - start;
        if (diff > 30 * 60 * 1000) {
            setErrorMsg("Khoảng thời gian chỉ được phép trong vòng 30 phút.");
            return null;
        }

        return {
            day: formatDayForAPI(day),
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            client_message_id: clientMsgId || null,
            page,
            page_size: pageSize,
        };
    };

    const fetchLogs = async () => {
        const payload = buildPayload();
        if (!payload) return;

        try {
            setLoading(true);
            setLogs([]);

            const res = await axios.post("/logs/by-day", payload);
            setLogs(res.data || []);
        } catch (err) {
            setErrorMsg(
                err.response?.data?.detail || "Lỗi khi gọi API /logs/by-day"
            );
        } finally {
            setLoading(false);
        }
    };

    // Khi đổi page hoặc pageSize → fetch lại
    useEffect(() => {
        if (!filters.day) return;
        fetchLogs();
    }, [page, pageSize]);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-800 px-6 py-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                        Tra cứu log
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Tìm log theo khoảng thời gian và Message ID.
                    </p>
                </div>
                <span
                    className={`text-sm px-3 py-1 rounded-full border ${
                        health.includes("OK")
                            ? "border-green-500 bg-green-50 text-green-700"
                            : "border-red-500 bg-red-50 text-red-700"
                    }`}
                >
                    {health}
                </span>
            </div>

            {/* Filters */}
            <Filters
                filters={filters}
                setFilters={setFilters}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onSubmit={() => {
                    setPage(1);
                    fetchLogs();
                }}
                loading={loading}
            />

            {/* Error */}
            {errorMsg && <ErrorBox message={errorMsg} />}

            {/* Table */}
            <LogsTable logs={logs} loading={loading} />

            {/* Pagination */}
            <Pagination
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                dataLength={logs.length}
                loading={loading}
            />
        </div>
    );
}
